<template>
  <PropertyNotFound v-if="propertyStore.isMissing(propertyId)" />

  <PageContainer v-else-if="renterProfileStore.isRenterMissing(renterId)" class="renter-not-found">
    <!-- Worded alike for a stale link and another company's renter: the backend answers 404 to both -->
    <EmptyState
      icon="user-01"
      title="This renter isn't in your records"
      description="The link points at a renter who never contacted your company."
    >
      <template #action>
        <Button variant="outline" size="medium" :to="propertyRoute(propertyId)">Back to the property</Button>
      </template>
    </EmptyState>
  </PageContainer>

  <PageContainer v-else @click.capture="guardManageAction">
    <View direction="column" gap="sm">
      <PageBackNav :teleport-slot-id="headerTeleportSlot" @back-nav="goBack(propertyRoute(propertyId))" />

      <View direction="row" class="wrapper" justify="space-between" gap="xl" v-if="renter && property">
        <View direction="column" gap="md" :grow="1">
          <RenterProfileHeader :renter="renter" :property="property" />
          <RenterProfileApplications />
          <RenterProfileInfoItemsCard id="rp-highlights-card" title="Highlights" :info-items="renterHighlights">
            <template v-slot:badges>
              <RenterProfileHighlightsBadges :badges="renterAssessment?.processedBadgesList || []" />
            </template>
            <template v-slot:highlights>
              <RenterProfileDialogHighlights
                v-if="renterScreening.data?.value?.convoHighlights.length"
                :highlights="renterScreening.data?.value?.convoHighlights"
              />
            </template>
          </RenterProfileInfoItemsCard>
          <RenterProfileInfoItemsCard title="Contact info" :info-items="contactInfoItems" />
        </View>

        <View direction="column" gap="xl" class="aside">
          <RenterProfilePropertyInquiries />
          <RenterProfileActivityFeed />
        </View>
      </View>
    </View>
  </PageContainer>
</template>

<script setup lang="ts">
import { computed, onUnmounted, watch } from 'vue';
import { useRoute } from 'vue-router';

import { Button, EmptyState, PageBackNav, PageContainer, View } from '@tallo/design-system';
import { propertyRoute, useNavigation } from '@tallo/investor/navigation';
import { PropertyNotFound, usePropertyStore, useManageActionGuard } from '@tallo/investor/property';
import { renterProfileAssessmentService } from '@tallo/investor/renter';

import productTourService from '../product-tour/product-tour.service';
import { headerTeleportSlot } from '../root/header-teleport-slot.const';
import RenterProfileActivityFeed from './components/renter-profile-activity-feed.vue';
import RenterProfileApplications from './components/renter-profile-applications.vue';
import RenterProfileDialogHighlights from './components/renter-profile-dialog-highlights.vue';
import RenterProfileHeader from './components/renter-profile-header.vue';
import RenterProfileHighlightsBadges from './components/renter-profile-highlights-badges.vue';
import RenterProfileInfoItemsCard from './components/renter-profile-info-items-card.vue';
import RenterProfilePropertyInquiries from './components/renter-profile-property-inquiries.vue';
import { useRenterScreeningQuery } from './queries/renter-screening.query';
import { useRenterProfileStore } from './store/renter-profile.store';

const route = useRoute();

const renterId = computed(() => route.params.renterId as string);
const propertyId = computed(() => route.params.propertyId as string);
// Accepting a showing, sending or deciding an application: stopped for a colleague who can only view the unit
const guardManageAction = useManageActionGuard(() => propertyId.value);

const renterProfileStore = useRenterProfileStore();
const propertyStore = usePropertyStore();
const renterScreening = useRenterScreeningQuery();
const { goBack } = useNavigation();

const renter = computed(() => renterProfileStore.getRenterProfile);
const property = computed(() => propertyStore.property);
const renterAssessment = computed(() => {
  return renterProfileAssessmentService.assessRenterProfile({
    renter: renter.value,
    property: property.value,
    screening: renterScreening.data?.value,
  });
});

const contactInfoItems = computed<{ label: string; value: string | number | undefined }[]>(() => [
  { label: 'Phone number', value: renter.value?.user.phoneNumber || 'N/A' },
  { label: 'Email', value: renter.value?.user.email || 'N/A' },
]);

const renterHighlights = computed<{ label: string; value: string | number | undefined }[]>(() => [
  { label: 'Credit score', value: renterAssessment.value?.creditScore.uiRepresentation },
  { label: 'Desired lease term', value: renter.value?.desiredLeaseTerm || 'N/A' },
  { label: 'Monthly income', value: renterAssessment.value?.income.uiRepresentation },
  {
    label: 'Desired lease date',
    value: renterAssessment.value?.moveInDate.uiRepresentation,
  },
  { label: 'Occupation', value: renter.value?.occupation || 'N/A' },
  {
    label: 'Pets',
    value: renterAssessment.value?.pets.uiRepresentation,
  },
  {
    label: 'Housing voucher',
    value: renterAssessment.value?.housingVoucher.uiRepresentation,
  },
  {
    label: 'ID verification',
    value: renterAssessment.value?.identityVerification.uiRepresentation,
  },
]);

watch(
  propertyId,
  (newPropertyId) => {
    if (newPropertyId) {
      propertyStore.$reset();
      propertyStore.loadProperty(newPropertyId);
    }
  },
  { immediate: true },
);

watch(
  renterId,
  (newRenterId) => {
    if (newRenterId) {
      renterProfileStore.$reset();
      renterProfileStore.load(newRenterId);
    }
  },
  { immediate: true },
);

watch(
  [renter, property],
  ([newRenter, newProperty]) => {
    if (newRenter && newProperty) {
      productTourService.tryStartRenterProfileTour();
    }
  },
  { immediate: true },
);

onUnmounted(() => {
  productTourService.stopActiveTour('renterProfile');
  renterProfileStore.$reset();
  propertyStore.$reset();
});
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

.renter-not-found {
  display: flex;
  justify-content: center;
  padding-top: var(--t-spacing-xxl);
}

.wrapper {
  @include screen-max-lg {
    flex-direction: column;
    gap: var(--t-spacing-md);
  }
}

.aside {
  max-width: 28.75rem;
  width: 100%;

  @include screen-max-lg {
    max-width: 100%;
  }
}
</style>
